import React, { useEffect, useState } from "react";
import MoonLoader from "react-spinners/MoonLoader";
import Swal from "sweetalert2";
import { useCRM } from "../CRMContext";
import CRMHeader from "./CRMHeader";
import SavedViews from "./SavedViews";
import Board from "./Board";
import ListView from "./ListView";
import Dashboard from "./Dashboard";
import AutomationsList from "./AutomationsList";
import FilterPanel from "./FilterPanel";
import ContactDrawer from "./ContactDrawer";
import BulkActionsBar from "./BulkActionsBar";
import ColumnForm from "./ColumnForm";
import ContactForm from "./ContactForm";
import PipelineManagementModal from "./PipelineManagementModal";
import { createColumn, updateColumn, deleteColumn, moveAllContacts } from "../services/pipeline.service";

const CRMShell = () => {
  const {
    state,
    loadPipelines,
    loadColumnsAndContacts,
    loadTeamMembers,
    addColumnToState,
    updateColumnInState,
    removeColumnFromState,
    addContactToColumn,
    showToast,
    refreshBoard,
  } = useCRM();

  // Modal states
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [editingColumn, setEditingColumn] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactColumnId, setContactColumnId] = useState(null);
  const [managePipelinesOpen, setManagePipelinesOpen] = useState(false);

  // Init
  useEffect(() => {
    const init = async () => {
      const pipelineId = await loadPipelines();
      if (pipelineId) await loadColumnsAndContacts(pipelineId);
      loadTeamMembers();
    };
    init();
  }, [loadPipelines, loadColumnsAndContacts, loadTeamMembers]);

  // Escutar novos contatos via WebSocket para atualizar o board em tempo real
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.newCrmContact) {
        const contact = event.data.newCrmContact;
        if (contact.column) {
          addContactToColumn(contact.column, contact);
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [addContactToColumn]);

  // Column CRUD handlers
  const handleAddColumn = () => {
    setEditingColumn(null);
    setColumnModalOpen(true);
  };

  const handleEditColumn = (column) => {
    setEditingColumn(column);
    setColumnModalOpen(true);
  };

  const handleSaveColumn = async (data) => {
    try {
      if (data.id) {
        const updated = await updateColumn(data.id, {
          name: data.name,
          color: data.color,
          probability: data.probability,
          max_days: data.max_days,
          stage_type: data.stage_type,
        });
        updateColumnInState(updated);
        showToast("Coluna atualizada!");
      } else {
        const maxOrder = state.columns.length > 0 ? Math.max(...state.columns.map((c) => c.order)) : -1;
        const created = await createColumn({
          name: data.name,
          color: data.color,
          probability: data.probability,
          max_days: data.max_days,
          stage_type: data.stage_type,
          order: maxOrder + 1,
          pipeline: state.activePipelineId,
        });
        addColumnToState(created);
        showToast("Coluna criada!");
      }
      setColumnModalOpen(false);
    } catch (err) {
      const msg = err?.response?.data?.error || "Erro ao salvar coluna.";
      showToast(msg, "error");
    }
  };

  const handleDeleteColumn = (column) => {
    const contacts = state.contactsByColumn[column.id] || [];
    if (contacts.length > 0) {
      Swal.fire({ title: "Não é possível excluir", text: "A coluna contém contatos. Mova-os primeiro.", icon: "warning", confirmButtonText: "OK" });
      return;
    }
    if (state.columns.length <= 1) {
      Swal.fire({ title: "Não é possível excluir", text: "É necessário ter pelo menos uma coluna.", icon: "warning", confirmButtonText: "OK" });
      return;
    }

    Swal.fire({
      title: "Confirmar exclusão",
      text: "Deseja excluir esta coluna?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef4444",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteColumn(column.id);
          removeColumnFromState(column.id);
          showToast("Coluna excluída!");
        } catch {
          showToast("Erro ao excluir coluna.", "error");
        }
      }
    });
  };

  const handleMoveAll = (column) => {
    const otherColumns = state.columns.filter((c) => c.id !== column.id);
    if (otherColumns.length === 0) {
      showToast("Não há outra coluna para mover.", "warning");
      return;
    }

    const inputOptions = {};
    otherColumns.forEach((c) => { inputOptions[c.id] = c.name; });

    Swal.fire({
      title: "Mover todos os contatos",
      text: `Mover todos os contatos de "${column.name}" para:`,
      input: "select",
      inputOptions,
      showCancelButton: true,
      confirmButtonText: "Mover",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed && result.value) {
        try {
          await moveAllContacts(column.id, result.value);
          showToast("Contatos movidos!");
          refreshBoard();
        } catch {
          showToast("Erro ao mover contatos.", "error");
        }
      }
    });
  };

  // Contact creation
  const handleAddContact = (columnId) => {
    setContactColumnId(columnId);
    setContactModalOpen(true);
  };

  const handleNewContact = () => {
    const firstCol = state.columns[0];
    setContactColumnId(firstCol?.id);
    setContactModalOpen(true);
  };

  // Active filter pills
  const activeFilterKeys = Object.keys(state.filters).filter(
    (k) => state.filters[k] !== undefined && state.filters[k] !== null && state.filters[k] !== ""
  );

  // Main content area
  const renderContent = () => {
    if (state.viewMode === "list") return <ListView />;
    if (state.viewMode === "dashboard") return <Dashboard />;
    if (state.viewMode === "automations") return <AutomationsList />;

    return (
      <Board
        onAddContact={handleAddContact}
        onEditColumn={handleEditColumn}
        onDeleteColumn={handleDeleteColumn}
        onAddColumn={handleAddColumn}
        onMoveAll={handleMoveAll}
      />
    );
  };

  return (
    <div className="crm-container">
      <CRMHeader onNewContact={handleNewContact} onManagePipelines={() => setManagePipelinesOpen(true)} />

      <SavedViews />

      {/* Active filter pills */}
      {activeFilterKeys.length > 0 && (
        <div className="crm-filters__active-pills">
          {activeFilterKeys.map((key) => (
            <span key={key} className="crm-filters__pill">
              {key}: {String(state.filters[key])}
            </span>
          ))}
        </div>
      )}

      {/* Main area with optional filter panel */}
      {state.loading ? (
        <div className="crm-loading">
          <MoonLoader color="#3b82f6" size={40} />
        </div>
      ) : state.error ? (
        <div className="crm-empty">
          <span className="crm-empty__title">Erro</span>
          <span className="crm-empty__desc">{state.error}</span>
          <button className="crm-btn crm-btn--primary" style={{ marginTop: 16 }} onClick={refreshBoard}>
            Tentar novamente
          </button>
        </div>
      ) : state.pipelines.length === 0 ? (
        <div className="crm-empty">
          <span className="crm-empty__title">Nenhum pipeline encontrado</span>
          <span className="crm-empty__desc">Crie seu primeiro pipeline para começar a usar o CRM.</span>
        </div>
      ) : (
        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
          <FilterPanel />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {renderContent()}
          </div>
        </div>
      )}

      {/* Drawer */}
      <ContactDrawer />

      {/* Bulk Actions */}
      <BulkActionsBar />

      {/* Column Modal */}
      {columnModalOpen && (
        <ColumnForm
          column={editingColumn}
          onSave={handleSaveColumn}
          onClose={() => setColumnModalOpen(false)}
        />
      )}

      {/* Contact Form Modal */}
      {contactModalOpen && (
        <ContactForm
          columnId={contactColumnId}
          onClose={() => setContactModalOpen(false)}
          onSaved={() => setContactModalOpen(false)}
        />
      )}

      {/* Pipeline Management Modal */}
      {managePipelinesOpen && (
        <PipelineManagementModal onClose={() => setManagePipelinesOpen(false)} />
      )}
    </div>
  );
};

export default CRMShell;
